import React from 'react';
import axios from 'axios';

import AppStorage from '../Storage/Storage';
import InputSwitcher from './InputSwitcher/InputSwitcher';
import Modal from './Modal/Modal';
import Requests from '../Requests/Requests';
import { KeycloakInstanceWrapper } from '../Keycloak/Keycloak';
import UserRoles from '../enums/UserRoles';
import { AdminSetting } from '../interfaces/AdminSettings';
import AdminLabels, {
  AdminPossibleSettingsActions,
  OptionTitle,
  AdminPossibleSettings,
} from './AdminLabels';

import './Admin.css';
import { HeaderWrapper } from '../Header/HeaderWrapper';

interface AdminState {
  isModalShown: boolean;
  settings: Array<AdminSetting>;
  selectedSetting: AdminSetting | null;
  isPermissionRestricted: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-types
class Admin extends React.PureComponent<{}, AdminState> {
  private readonly storage: AppStorage;

  constructor(props: {}) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.state = {
      isModalShown: false,
      settings: [],
      selectedSetting: null,
      isPermissionRestricted: false,
    };
  }

  componentDidMount(): void {
    const keycloakInstance = KeycloakInstanceWrapper.getInstance();
    if (keycloakInstance && !keycloakInstance.authenticated) {
      void keycloakInstance.login({
        loginHint: 'admin',
      });
    } else if (keycloakInstance) {
      const isRole = keycloakInstance.hasRealmRole(UserRoles.QS_ADMIN);
      if (isRole) {
        this.getSettings();
      } else {
        this.setState({
          isPermissionRestricted: true,
        });
      }
    }
  }

  getSettings(): void {
    void Requests.getAdminSetting()
      .then((response) => {
        // eslint-disable-next-line no-underscore-dangle
        const { settings } = response.data?.result._embedded;
        this.setState({
          settings,
        });
      });
  }

  onChangeHandler = (setting: AdminSetting): void => {
    this.setState({
      selectedSetting: setting,
    });
    this.openModal();
  };

  switchSetting = (): void => {
    const {
      selectedSetting: setting,
    } = this.state;

    if (setting) {
      const {
        href: url,
        type: method,
        // eslint-disable-next-line no-underscore-dangle
      } = setting._links.toggle;

      void axios({
        method,
        url,
        headers: {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          Authorization: `Bearer ${KeycloakInstanceWrapper.getInstance().token}`,
        },
      }).then(() => {
        this.getSettings();
      });
    }
  };

  resetSelectedSetting = (): void => {
    this.setState({
      selectedSetting: null,
    });
  };

  rejectConfirmation = (): void => {
    this.resetSelectedSetting();
    this.closeModal();
  };

  agreeConfirmation = (): void => {
    this.switchSetting();
    this.resetSelectedSetting();
    this.closeModal();
  };

  closeModal = (): void => {
    this.setState({
      isModalShown: false,
    });
  };

  openModal = (): void => {
    this.setState({
      isModalShown: true,
    });
  };

  public static HEADER = 'Admin console';

  public static NO_PERMISSION = 'You have no permissions';

  render(): React.ReactElement {
    const {
      isModalShown,
      settings,
      selectedSetting,
      isPermissionRestricted,
    } = this.state;

    const mode = selectedSetting?.enabled ? 'OFF' : 'ON';
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const labels = AdminLabels[`${selectedSetting?.name}_${mode}` as AdminPossibleSettingsActions];

    return (
      <div className="App">
        <HeaderWrapper />
        <div className="App-content">
          <div className="admin-screen">
            <h3>
              { Admin.HEADER }
            </h3>
            {isPermissionRestricted && (
              <p>
                { Admin.NO_PERMISSION }
              </p>
            )}
            {settings.map((setting) => (
              <InputSwitcher
                key={setting.id}
                value={setting.enabled}
                label={OptionTitle[setting.name as AdminPossibleSettings]}
                onChange={(): void => {
                  this.onChangeHandler(setting);
                }}
              />
            ))}
          </div>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*// @ts-ignore*/}
          <Modal isShown={isModalShown}>
            <div className="confirmation">
              <h5>
                {labels?.confirmationTitle}
              </h5>
              <p>{labels?.confirmation}</p>
              <footer>
                <span
                  role="presentation"
                  onClick={this.rejectConfirmation}
                >
                  No
                </span>
                <span
                  role="presentation"
                  onClick={this.agreeConfirmation}
                >
                  Yes
                </span>
              </footer>
            </div>
          </Modal>
        </div>
      </div>
    );
  }
}

export default Admin;
